import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import enTranslations from '../translations/en.json';
import deTranslations from '../translations/de.json';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// All translations loaded from JSON files.
// Branded content (welcome messages, brand name) comes from BrandingContext.
const translations = {
  en: enTranslations,
  de: deTranslations,
};

// Key used to track whether the user has ever explicitly chosen a language.
// Without this, we can't tell "user picked English" from "nobody chose yet".
const EXPLICIT_CHOICE_KEY = 'ui-language-explicit';

export const LanguageProvider = ({ children }) => {
  // Start with localStorage if user explicitly chose before, otherwise 'en'
  // as a temporary default until branding loads with the real default.
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('ui-language') || 'en';
  });

  useEffect(() => {
    localStorage.setItem('ui-language', language);
  }, [language]);

  // Called by BrandingContext once it knows the configured default language.
  // Only applies if the user has never explicitly switched languages.
  const applyBrandingDefault = useCallback((defaultLang) => {
    if (!defaultLang) return;
    const userChoseExplicitly = localStorage.getItem(EXPLICIT_CHOICE_KEY);
    if (!userChoseExplicitly && translations[defaultLang]) {
      setLanguage(defaultLang);
    }
  }, []);

  // Translation function — returns the value for the current language,
  // falling back to the key itself if not found.
  const t = (key) => {
    return translations[language]?.[key] ?? key;
  };

  // Explicit user action — remember that they chose, so we don't override it.
  const switchLanguage = (newLanguage) => {
    localStorage.setItem(EXPLICIT_CHOICE_KEY, 'true');
    setLanguage(newLanguage);
  };

  return (
    <LanguageContext.Provider value={{ language, switchLanguage, t, applyBrandingDefault }}>
      {children}
    </LanguageContext.Provider>
  );
};
